import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ArrowLeft, CreditCard, Crown, Check, ArrowsClockwise } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import BottomNav from '../components/BottomNav';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PaymentsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [packages, setPackages] = useState({});
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(null);
  const [activeTab, setActiveTab] = useState('plans');

  useEffect(() => {
    fetchPackages();
    fetchHistory();
    // Check for returning from Stripe
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    if (sessionId) pollStatus(sessionId);
  }, []);

  const fetchPackages = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/payments/packages`, { withCredentials: true });
      setPackages(data);
    } catch (err) { /* silent */ }
  };

  const fetchHistory = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/payments/history`, { withCredentials: true });
      setHistory(data);
    } catch (err) { /* silent */ }
  };

  const checkout = async (packageId) => {
    setLoading(packageId);
    try {
      const { data } = await axios.post(`${API_URL}/api/payments/checkout`, {
        package_id: packageId,
        origin_url: window.location.origin
      }, { withCredentials: true });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Checkout failed');
      setLoading(null);
    }
  };

  const pollStatus = async (sessionId, attempts = 0) => {
    if (attempts >= 5) return;
    try {
      const { data } = await axios.get(`${API_URL}/api/payments/status/${sessionId}`, { withCredentials: true });
      if (data.payment_status === 'paid') {
        toast.success('Payment successful! Thank you!');
        fetchHistory();
        navigate('/payments', { replace: true });
      } else if (data.status === 'expired') {
        toast.error('Payment expired');
      } else {
        setTimeout(() => pollStatus(sessionId, attempts + 1), 2000);
      }
    } catch (err) {
      setTimeout(() => pollStatus(sessionId, attempts + 1), 2000);
    }
  };

  const pkgList = [
    { id: 'premium_monthly', icon: Crown, color: '#9D4CDD', desc: 'Ad-free, priority matchmaking, exclusive badges' },
    { id: 'premium_yearly', icon: Crown, color: '#FACC15', desc: 'Same benefits, save 33% annually' },
    { id: 'tournament_entry', icon: CreditCard, color: '#00F0FF', desc: 'Entry fee for competitive tournaments' },
  ];

  return (
    <div className="app-container" data-testid="payments-page">
      <header className="p-6 pb-3">
        <div className="flex items-center gap-3 mb-4">
          <button onClick={() => navigate(-1)} className="w-10 h-10 rounded-full bg-[#181824] flex items-center justify-center">
            <ArrowLeft size={20} weight="bold" className="text-white" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-white flex items-center gap-2" style={{ fontFamily: 'Unbounded, sans-serif' }}>
              <CreditCard size={24} weight="duotone" className="text-[#9D4CDD]" />
              Payments
            </h1>
            <p className="text-[#71717A] text-xs">Premium & tournament fees</p>
          </div>
        </div>
        <div className="flex gap-2">
          {['plans', 'history'].map(t => (
            <button key={t} onClick={() => setActiveTab(t)}
              className={`flex-1 py-2 rounded-xl text-xs font-medium transition-all ${activeTab === t ? 'bg-gradient-to-br from-[#9D4CDD] to-[#7C3AED] text-white' : 'bg-[#181824] text-[#A1A1AA]'}`}>
              {t === 'plans' ? 'Plans' : 'History'}
            </button>
          ))}
        </div>
      </header>

      <div className="px-6 pb-32 space-y-3">
        {activeTab === 'plans' && (
          <>
            {user?.premium && (
              <div className="p-3 rounded-xl bg-[#9D4CDD]/10 border border-[#9D4CDD]/30 flex items-center gap-2">
                <Crown size={20} weight="fill" className="text-[#9D4CDD]" />
                <span className="text-white text-sm font-medium">Premium Active</span>
              </div>
            )}
            {pkgList.map(({ id, icon: Icon, color, desc }) => {
              const pkg = packages[id];
              if (!pkg) return null;
              return (
                <motion.div key={id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                  className="p-4 rounded-xl bg-[#0F0F16] border border-white/5">
                  <div className="flex items-center gap-3 mb-2">
                    <Icon size={24} weight="duotone" style={{ color }} />
                    <div className="flex-1">
                      <h3 className="text-white text-sm font-semibold">{pkg.name}</h3>
                      <p className="text-[#71717A] text-[10px]">{desc}</p>
                    </div>
                    <span className="text-white font-bold">${pkg.amount.toFixed(2)}</span>
                  </div>
                  <button onClick={() => checkout(id)} disabled={loading === id}
                    className="w-full py-2 rounded-lg text-xs font-medium transition-all"
                    style={{ background: `${color}20`, color, border: `1px solid ${color}30` }}
                    data-testid={`checkout-${id}`}>
                    {loading === id ? <ArrowsClockwise size={14} className="animate-spin mx-auto" /> : 'Purchase'}
                  </button>
                </motion.div>
              );
            })}
          </>
        )}

        {activeTab === 'history' && (
          history.length > 0 ? history.map((tx, i) => (
            <div key={i} className="p-3 rounded-xl bg-[#0F0F16] border border-white/5">
              <div className="flex justify-between mb-1">
                <span className="text-white text-xs">{tx.metadata?.package_name || tx.package_id}</span>
                <span className={`text-[10px] px-2 py-0.5 rounded-full ${
                  tx.payment_status === 'paid' ? 'bg-[#39FF14]/10 text-[#39FF14]' : 'bg-[#FACC15]/10 text-[#FACC15]'
                }`}>{tx.payment_status}</span>
              </div>
              <div className="flex justify-between text-[10px] text-[#71717A]">
                <span>${tx.amount?.toFixed(2)} {tx.currency?.toUpperCase()}</span>
                <span>{new Date(tx.created_at).toLocaleDateString()}</span>
              </div>
            </div>
          )) : (
            <p className="text-center text-[#71717A] py-8 text-sm">No payment history</p>
          )
        )}
      </div>

      <BottomNav />
    </div>
  );
}
